import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../../../config/api';
import { getCsrfToken } from '../../../config/csrf';

const IcibAdminContext = createContext(null);

/* Backend wiring for the ICIB admin console, scoped to BrandonUserStoryAPI.md's
   ICIB-01 through ICIB-11 (README.md §4 for the underlying endpoint table).

   State is a single `db` object, refetched from the real per-resource
   endpoints (§4 of README.md — there is no single aggregate "dashboard"
   endpoint in the documented contract): global collections (groups, the
   caller's own profile) plus, for whichever group is currently selected,
   its own products (GroupProduct rows — there is no more shared product
   catalog; every GroupProduct is fully self-contained per §2.9/§2.10).
   Three on-demand caches (`enrolmentDetails`, `templateDetails`,
   `employeeDetails`) cover endpoints that resolve a single record by id
   rather than a list — GET /enrolments/{id} (ICIB-03b), GET
   /form-templates/{id} (ICIB-10c), and GET /companies/{gid}/employees/{id}
   (ICIB-01b, the drill-down: form_data + enrolments for one person) —
   fetched only when a view actually needs that record.

   GET /users, GET /companies/{gid}/employees/summary, GET
   /companies/{gid}/form-templates, GET /notifications?company={gid}, and GET
   /companies/{gid}/enrolments (ICIB-12) are each deliberately their own
   thing, outside `db`/`refresh()` entirely — see the users/employees/
   templates/notifications/groupEnrolments state blocks below. Each is
   only read by a handful of views (e.g. GET /users only by UsersView),
   so each is fetched lazily — by whichever view's mount effect actually
   needs it — rather than riding along in a global batch on every
   dashboard load / group switch / unrelated mutating action.

   ICIB-12 (GET /companies/{gid}/enrolments) is what Confirmations reads —
   a lean, group-wide enrolment book (enrolment_id, employee_name,
   product *name*, status; one row per enrolment) — instead of the
   N-employee-detail-fetches-per-load pattern every other enrolment view
   still uses (ICIB-01a's per-group employee summary + an ICIB-01b
   drill-down per employee, via ensureEmployeeDetails).

   Every mutating call refetches whatever it actually changed afterwards
   (via refresh(), usersMutating(), or mutatingRefresh() with the specific
   loadEmployees/loadTemplates/loadNotifications loader(s) that action's
   endpoint actually affects) rather than hand-patching local state, to
   stay honest about each endpoint's actual response shape.

   Pagination: ICIB-08l documents real limit/offset paging (default 50,
   capped 200) for GET /users, and says GET /notifications and GET
   /companies/{gid}/employees/summary share the same contract; ICIB-03a's own
   entry confirms the same limit/offset shape directly for /notifications
   too. fetchAllPages below loops through every page for whichever of
   those three a call asks for, so the result is always the complete list
   for that resource (every list view already paginates the *display*
   client-side via components/Pagination.js).

   There is no more `GET /products` master catalog endpoint (§2.9 removed)
   — `GET /companies/{gid}/products` (ICIB-11f) already returns each
   GroupProduct's full self-contained record (name, insurer_name,
   insurer_link, insurer_scheme_reference, eligibility_days,
   information_pack_link, email_body_template) in the list itself, so
   there's no separate full-detail fetch to cache any more either. */

const EMPTY_DB = {
  me: null,
  groups: [],
  groupProducts: [],  // GroupProduct rows for the *current* group (§2.10) — fully self-contained, no shared catalog
  audit: [],
  // Per-resource load failures, keyed the same as the fields above (e.g.
  // errors.products) — a resource that failed to load keeps an empty array
  // above rather than blanking the whole dashboard, so a view can still
  // render its create form / other sections and just show its own section
  // as unavailable. See `refresh` below.
  errors: {},
};

// Django's CsrfViewMiddleware (README's stack — DRF + session auth) rejects
// any unsafe-method request (POST/PUT/PATCH/DELETE) that doesn't carry an
// X-CSRFToken header matching the server's token. Not read from
// document.cookie — the SPA and API are on different registrable domains in
// every deployed environment, so the browser never exposes the API's
// csrftoken cookie to this page's JS. getCsrfToken() (config/csrf.js) holds
// the token AuthContext fetched from GET /auth/csrf instead. Safe methods
// don't need it.
async function icibFetch(path, { method = 'GET', body } = {}) {
  let res;
  try {
    const unsafe = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
    // Omit the header entirely when there's no token yet, rather than sending
    // '' — an empty X-CSRFToken header fails Django's length check ("incorrect
    // length") instead of the clearer "CSRF token missing" an absent header gets.
    const csrfToken = unsafe ? getCsrfToken() : null;
    res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    return { error: 'network_error', message: 'Could not reach the server. Check your connection and try again.' };
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    // Most non-2xx bodies carry a flat { error, message } (single-item
    // shape) — pass those through as-is. But the bulk-upload all-or-nothing
    // rejection (§2.6) has no top-level `error` at all, just
    // { committed: false, results: [...] } with each failing row's real
    // message inside results[i].message — discarding that body (as this
    // used to, falling back to a generic "Request failed (422)" whenever
    // `.error` was missing) threw away the only place the actual reason
    // lived. Trust any JSON body the server sent; only synthesize a
    // generic error when there's truly nothing to show (JSON parse failed).
    return data || { error: 'request_failed', message: `Request failed (${res.status}).` };
  }
  return data;
}

// GET .../template endpoints (ICIB-08g) return a CSV file, not JSON —
// fetch and hand it to the browser as a download rather than through icibFetch.
// GET is a safe method, so no CSRF header needed here.
async function icibDownload(path, filename) {
  let res;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, { credentials: 'include' });
  } catch (err) {
    return { error: 'network_error', message: 'Could not reach the server. Check your connection and try again.' };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    return data && data.error ? data : { error: 'request_failed', message: `Request failed (${res.status}).` };
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return null;
}

// List endpoints in README §4 aren't all shown with a worked example body —
// some (§ICIB-09a, §ICIB-03a, §ICIB-10a) are; others (GET /products, GET
// /companies/{id}/employees, GET /companies/{id}/products) are only described.
// Read defensively under a couple of plausible shapes rather than assuming
// one, so a real backend that varies slightly doesn't blank the view.
function arr(data, ...keys) {
  if (Array.isArray(data)) return data;
  for (const k of keys) if (Array.isArray(data?.[k])) return data[k];
  return [];
}

// BrandonUserStoryAPI.md ICIB-08l documents real server-side pagination —
// limit/offset, response carries { count, limit, offset, <items> } — for
// GET /users, and says GET /notifications (§4.8) and GET /companies/{gid}/
// employees/summary (§4.4) share "the same contract" (default limit=50,
// capped at 200); ICIB-03a's own entry confirms the same limit/offset
// shape directly for /notifications. Every list view already renders off
// a fully-loaded array (client-side Pagination component slices it for
// display), so rather than rearchitect those views to fetch page-by-page,
// loop through every page here and hand back the complete list — correct
// against the real pagination contract without changing anything
// downstream.
//
// Dedupes by id across pages (see the loop below) as a defensive measure —
// a backend that doesn't honour `offset` (or a `count` that doesn't match
// what's actually behind the filter) would otherwise show every record
// duplicated once per page this loops through.
async function fetchAllPages(path, itemsKey) {
  const sep = path.includes('?') ? '&' : '?';
  const limit = 200;
  let offset = 0;
  let items = [];
  const seenIds = new Set();
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const res = await icibFetch(`${path}${sep}limit=${limit}&offset=${offset}`);
    if (res?.error) {
      // First page failing is a real failure for this resource. A later
      // page failing mid-loop just means "stop here" — keep what already
      // loaded rather than discarding it.
      if (offset === 0) return res;
      break;
    }
    const page = arr(res, itemsKey);
    // Dedupe by id as pages come in — a backend that doesn't actually
    // honour `offset` (echoing the same page back regardless) would
    // otherwise show every record duplicated once per page fetched.
    // Not every list item is guaranteed to use `id` as its identity field
    // (e.g. a bulk-action result row keys off `notification_id` instead) —
    // fall back through the other id-shaped fields this app's endpoints
    // actually use before giving up on dedup for that item.
    let newCount = 0;
    for (const item of page) {
      const id = item?.id ?? item?.notification_id ?? item?.enrolment_id ?? item?.employee_id ?? item?.user_id;
      if (id != null) {
        if (seenIds.has(id)) continue;
        seenIds.add(id);
      }
      items.push(item);
      newCount += 1;
    }
    const count = typeof res?.count === 'number' ? res.count : items.length;
    if (page.length < limit || items.length >= count || !page.length || !newCount) break;
    offset += limit;
  }
  return { [itemsKey]: items, count: items.length };
}

export function IcibAdminProvider({ children }) {
  const [db, setDb] = useState(EMPTY_DB);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  // Count of refresh() calls currently in flight — separate from `status`,
  // which only reflects the very first load (every view already renders
  // its own "Loading…" state off `status === 'loading'`, and that's meant
  // to stay a one-time thing, not flash on every group switch or mutating
  // action's refetch). This is what lets the shell show a lightweight
  // in-progress indicator for *every* refresh, first load or not.
  const [inFlightCount, setInFlightCount] = useState(0);
  const [groupId, setGroupId] = useState(null);
  const [enrolmentDetails, setEnrolmentDetails] = useState({}); // enrolment_id -> GET /enrolments/{id} result
  const [templateDetails, setTemplateDetails] = useState({}); // template_id -> GET /form-templates/{id} result (fields included)
  const [activeTemplates, setActiveTemplates] = useState({}); // company_id -> GET /companies/{gid}/form-templates?active=true result (template incl. fields), or null if the group has none yet
  const [groupAdmins, setGroupAdmins] = useState({}); // company_id -> GET /companies/{company_id}/employer-admins result
  const [employeeDetails, setEmployeeDetails] = useState({}); // employee_id -> GET /companies/{gid}/employees/{id} result (form_data + enrolments)
  const [availableTokens, setAvailableTokens] = useState({}); // company_id -> ICIB-11l GET /companies/{gid}/available-tokens result ({ static }) — form_fields removed, static-only now
  // GET /users, GET /companies/{gid}/employees/summary, GET /companies/{gid}/
  // form-templates, GET /notifications?company={gid} — each deliberately
  // kept out of `db`/`refresh()` (see the module comment above): fetched
  // lazily, only once a view that actually needs it calls the matching
  // loadX, rather than on every dashboard load / group switch / unrelated
  // mutating action.
  const [users, setUsers] = useState([]);
  const [usersStatus, setUsersStatus] = useState('idle'); // 'idle' | 'loading' | 'ready' | 'error'
  const [usersError, setUsersError] = useState(null);
  const [employees, setEmployees] = useState([]); // ICIB-01a summary — employee_id + user only, no form_data/enrolments
  const [employeesStatus, setEmployeesStatus] = useState('idle');
  const [employeesError, setEmployeesError] = useState(null);
  // ICIB-01a's own `enrolment_status` filter (documented alongside the
  // unfiltered summary above, but this story's original framing doesn't
  // use it — see loadEmployees) — kept as its own cache rather than
  // refiltering `employees` client-side, same reasoning as EMP-02a's
  // `outstanding` in EmployerContext.js: the summary carries no per-status
  // detail to filter by (just `has_active_enrolment`), and the two lists
  // are independently paginated over the wire.
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [filteredEmployeesStatus, setFilteredEmployeesStatus] = useState('idle');
  const [filteredEmployeesError, setFilteredEmployeesError] = useState(null);
  const [templates, setTemplates] = useState([]); // FormTemplate versions (no fields) for whichever group loadTemplates was last called for
  const [templatesStatus, setTemplatesStatus] = useState('idle');
  const [templatesError, setTemplatesError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationsStatus, setNotificationsStatus] = useState('idle');
  const [notificationsError, setNotificationsError] = useState(null);
  // ICIB-12 — GET /companies/{gid}/enrolments: a lean, group-wide enrolment
  // book (enrolment_id, employee_name, product *name*, status — one row
  // per enrolment, so a person with two products in the group appears
  // twice). Confirmations reads this directly instead of ICIB-01a's
  // employee summary + an ICIB-01b drill-down per employee.
  const [groupEnrolments, setGroupEnrolments] = useState([]);
  const [groupEnrolmentsStatus, setGroupEnrolmentsStatus] = useState('idle');
  const [groupEnrolmentsError, setGroupEnrolmentsError] = useState(null);
  // ICIB-13a — GET /companies/{gid}/pending-intake: every Addition/Update/
  // Removal row submitted with a future effective_date (§2.6a), for
  // whichever group loadPendingIntake was last called for. Fetched
  // unfiltered (same reasoning as loadGroupEnrolments above) — the
  // endpoint's own status/action filters aren't used here since the view
  // wants pending/processed/failed/cancelled all visible at once, filtered
  // client-side.
  const [pendingIntake, setPendingIntake] = useState([]);
  const [pendingIntakeStatus, setPendingIntakeStatus] = useState('idle');
  const [pendingIntakeError, setPendingIntakeError] = useState(null);
  // ICIB-15 — GET /companies/{gid}/overview: server-computed employee/
  // enrolment counts for one group (total_employees, fully_onboarded_employees,
  // employees_with_invited_enrolment, employees_with_removed_enrolment).
  // Cached per group, keyed by gid — OverviewView reads the currently
  // selected group's counts straight from here instead of deriving them
  // client-side from ICIB-01a/01b.
  const [groupOverview, setGroupOverview] = useState({});
  const [groupOverviewStatus, setGroupOverviewStatus] = useState('idle');
  const [groupOverviewError, setGroupOverviewError] = useState(null);
  const rateLog = useRef([]);
  // Bumped on every refresh() call, so an in-flight call can tell it's
  // been superseded once it finally resolves (see the guard below).
  const requestIdRef = useRef(0);
  // Same stale-response guard as requestIdRef above, one counter per
  // group-scoped lazy loader (loadEmployees/loadTemplates/loadNotifications/
  // loadGroupEnrolments). Each of those is called with an explicit `gid`
  // whenever its view mounts or the group changes — switching groups twice
  // in quick succession (e.g. group A -> group B before A's slower request
  // has resolved) would otherwise let A's response land *after* B's and
  // silently overwrite B's data with A's, even though the UI has already
  // moved on to B and the loader's status already flipped to 'ready'.
  const loadRequestIdRef = useRef({ employees: 0, filteredEmployees: 0, templates: 0, notifications: 0, groupEnrolments: 0, pendingIntake: 0 });
  // Which group's data each loader's state currently holds — lets each
  // loader tell "refetching the group already shown" (a mutation's
  // refetch, e.g. after adding an employee — keep status 'ready', no
  // loading flash) apart from "switching to a different group" (must show
  // 'loading', since the state these hold is about to become the *wrong*
  // group's data for however long the fetch takes). Without this, the
  // 's === "ready" ? "ready" : "loading"' status update below never
  // re-armed after the very first group was ever loaded — every
  // *subsequent* group switch left status stuck at 'ready' the whole time
  // the new group's fetch was in flight, during which the view kept
  // rendering the *previous* group's now-filtered-out-by-id rows as "No
  // matches" instead of a loading state — indistinguishable from "this
  // group genuinely has no data" until the fetch happened to finish.
  const loadedGroupRef = useRef({ employees: undefined, filteredEmployees: undefined, templates: undefined, notifications: undefined, groupEnrolments: undefined, pendingIntake: undefined });

  /* Fans out to every global read plus, for `gid` (null meaning "All
     groups" — the default, and a real choice the user can return to, not
     just an absence of one), the group-scoped reads. Called on mount,
     whenever the selected group changes, and after every mutating call. */
  const refresh = useCallback((gidOverride) => {
    const requestId = ++requestIdRef.current;
    setStatus((s) => (s === 'ready' ? 'ready' : 'loading'));
    setInFlightCount((c) => c + 1);
    return Promise.all([
      icibFetch('/auth/me'),
      icibFetch('/companies'),
    ]).then(async ([me, groupsRes]) => {
      // Fail soft, per resource: a broken/missing endpoint (a 404 because
      // the backend hasn't implemented it yet, a 500, a timeout) shouldn't
      // blank the entire console — every other tab, and this one's own
      // create form, should still work off whatever *did* load. Each
      // failed resource just comes back empty, with the reason recorded in
      // db.errors for the view to show inline if it wants to.
      const errors = {};
      const note = (key, res) => { if (res?.error) errors[key] = res.message || res.error; };
      note('groups', groupsRes);

      const groups = arr(groupsRes, 'companies');
      // No auto-pick-the-first-active-group fallback — "All groups" (gid
      // === null) is the real default and a real choice the scope
      // selector can go back to, not just a transient absence of one.
      const gid = gidOverride !== undefined ? gidOverride : groupId;

      // GET /companies/{gid}/employees/summary, /form-templates, and
      // /notifications?company={gid} are NOT fetched here any more — see the
      // module comment. Only groupProducts (attached-products list) stays
      // in the shared batch, since it isn't scoped to one tab the way
      // those three are.
      //
      // ?include_inactive=true (ICIB-11f) — icib_admin only (this console
      // always is), so always ask for it: without it a removed product's
      // row is left out entirely (and doesn't even carry an `is_active`
      // key), which is exactly the state ICIB-11n's reactivate needs to be
      // findable/actionable from this list.
      let groupProducts = [];
      if (gid) {
        const gpRes = await icibFetch(`/companies/${gid}/products?include_inactive=true`);
        note('groupProducts', gpRes);
        groupProducts = arr(gpRes, 'group_products', 'products').map((gp) => ({ ...gp, company_id: gid, id: gp.id || gp.product_id }));
      }

      if (Object.keys(errors).length) console.error('ICIB dashboard: some resources failed to load', errors);

      // Stale-response guard: the mount effect below fires refresh() a
      // second time right after this one resolves (see its comment) to
      // pick up group-scoped data for the group setGroupId just settled
      // on — if the user picks a *different* group via the scope selector
      // while that first, slower call is still in flight, its eventual
      // resolution would otherwise call setGroupId(gid) with its own
      // (now stale) group and silently snap the UI back to it a few
      // seconds after the user had already switched away. A newer
      // refresh() bumps requestIdRef past what this call captured, so an
      // older call that loses the race just discards its result instead
      // of applying it.
      if (requestIdRef.current !== requestId) return null;

      setDb({
        me: me && !me.error ? me : null,
        groups,
        groupProducts,
        audit: [],
        errors,
      });
      setGroupId(gid);
      setStatus('ready');
      return null;
    }).catch((err) => {
      if (requestIdRef.current !== requestId) return null;
      // Every individual fetch already fails soft (see above) — this only
      // catches a genuine bug in the code above, not an API/network error.
      console.error('ICIB dashboard load threw unexpectedly', err);
      setStatus('error');
      return { error: 'unexpected_error', message: 'Something went wrong loading the dashboard.' };
    }).finally(() => {
      setInFlightCount((c) => c - 1);
    });
  }, [groupId]);

  // Loads everything on mount (groupId starts null — "All groups"), and
  // refetches whenever the scope selector picks a different group (or
  // goes back to "All groups", also null).
  useEffect(() => {
    refresh(groupId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  // ICIB-08l — GET /users (paginated, limit/offset, capped 200 per page —
  // fetchAllPages walks every page). Lazy: only UsersView calls this, on
  // mount, rather than it riding along in refresh()'s global batch on
  // every dashboard load / group switch / unrelated mutating action.
  // This screen manages staff accounts only (icib_admin/employer_admin) —
  // every employee also gets a User row (§4.1), so an unfiltered GET
  // /users would pull in the whole employee base too, just to be filtered
  // back out client-side. ICIB-08l's `role` filter only takes one value
  // at a time, so fetch each staff role separately and merge rather than
  // fetching everyone.
  const loadUsers = useCallback(async () => {
    setUsersStatus((s) => (s === 'ready' ? 'ready' : 'loading'));
    const [admins, employerAdmins] = await Promise.all([
      fetchAllPages('/users?role=icib_admin', 'users'),
      fetchAllPages('/users?role=employer_admin', 'users'),
    ]);
    const failed = [admins, employerAdmins].find((r) => r?.error);
    if (failed) {
      setUsersStatus('error');
      setUsersError(failed.message || failed.error);
      return failed;
    }
    const merged = [...arr(admins, 'users'), ...arr(employerAdmins, 'users')];
    setUsers(merged);
    setUsersStatus('ready');
    setUsersError(null);
    return { users: merged };
  }, []);

  // ICIB-01a — GET /companies/{gid}/employees/summary (paginated). Lazy, per
  // group: called with an explicit `gid` (not read off the `groupId`
  // closure) by whichever view's mount effect needs the roster — Overview,
  // Employees, Confirmations, Intake's Update/Removal panels — so it
  // refetches correctly if that view re-mounts after the group changed.
  // Deliberately lightweight — employee_id + nested user only, no
  // form_data/enrolments; the drill-down for those is ICIB-01b
  // (loadEmployeeDetail/employeeDetails below).
  const loadEmployees = useCallback(async (gid) => {
    const requestId = ++loadRequestIdRef.current.employees;
    if (!gid) { setEmployees([]); setEmployeesStatus('ready'); setEmployeesError(null); loadedGroupRef.current.employees = null; return null; }
    // Only skip the loading flash when re-fetching the *same* group already
    // held — see loadedGroupRef above.
    const sameGroup = loadedGroupRef.current.employees === gid;
    setEmployeesStatus((s) => (sameGroup && s === 'ready' ? 'ready' : 'loading'));
    const res = await fetchAllPages(`/companies/${gid}/employees/summary`, 'employees');
    // Stale-response guard — see loadRequestIdRef above.
    if (loadRequestIdRef.current.employees !== requestId) return res;
    if (res?.error) {
      setEmployeesStatus('error');
      setEmployeesError(res.message || res.error);
      return res;
    }
    setEmployees(arr(res, 'employees').map((e) => ({ ...e, id: e.employee_id || e.id, company_id: gid, enrolments: [] })));
    setEmployeesStatus('ready');
    setEmployeesError(null);
    loadedGroupRef.current.employees = gid;
    return res;
  }, []);

  // ICIB-01a — GET /companies/{gid}/employees/summary?enrolment_status={status}.
  // Same endpoint/shape as loadEmployees above, just with the documented
  // `enrolment_status` filter applied — narrows *which* employees appear
  // (anyone with at least one enrolment in the given status), not what's
  // returned per employee. Mirrors EmployerContext's EMP-02a/loadOutstanding.
  // `status` is one of the five documented EmployeeEnrolment.Status values;
  // EmployeesView's dropdown defaults to "all", which just uses the
  // unfiltered loadEmployees/employeesIn above instead of calling this.
  const loadFilteredEmployees = useCallback(async (gid, status) => {
    const requestId = ++loadRequestIdRef.current.filteredEmployees;
    if (!gid || !status) { setFilteredEmployees([]); setFilteredEmployeesStatus('ready'); setFilteredEmployeesError(null); loadedGroupRef.current.filteredEmployees = null; return null; }
    const key = `${gid}:${status}`;
    const sameKey = loadedGroupRef.current.filteredEmployees === key;
    setFilteredEmployeesStatus((s) => (sameKey && s === 'ready' ? 'ready' : 'loading'));
    const res = await fetchAllPages(`/companies/${gid}/employees/summary?enrolment_status=${status}`, 'employees');
    if (loadRequestIdRef.current.filteredEmployees !== requestId) return res;
    if (res?.error) {
      setFilteredEmployeesStatus('error');
      setFilteredEmployeesError(res.message || res.error);
      return res;
    }
    setFilteredEmployees(arr(res, 'employees').map((e) => ({ ...e, id: e.employee_id || e.id, company_id: gid, enrolments: [] })));
    setFilteredEmployeesStatus('ready');
    setFilteredEmployeesError(null);
    loadedGroupRef.current.filteredEmployees = key;
    return res;
  }, []);

  // ICIB-10a — GET /companies/{gid}/form-templates. Lazy, per group — called
  // by Forms, and Intake's single-item Addition form (activeTemplate, for
  // its per-field label/type/required inputs — see AdditionPanel).
  // Products and Intake's bulk-upload panel use the leaner ICIB-11l
  // GET /companies/{gid}/available-tokens instead, since they only need
  // field_key strings, not full field metadata.
  const loadTemplates = useCallback(async (gid) => {
    const requestId = ++loadRequestIdRef.current.templates;
    if (!gid) { setTemplates([]); setTemplatesStatus('ready'); setTemplatesError(null); loadedGroupRef.current.templates = null; return null; }
    // Only skip the loading flash when re-fetching the *same* group already
    // held — see loadedGroupRef above.
    const sameGroup = loadedGroupRef.current.templates === gid;
    setTemplatesStatus((s) => (sameGroup && s === 'ready' ? 'ready' : 'loading'));
    const res = await icibFetch(`/companies/${gid}/form-templates`);
    // Stale-response guard — see loadRequestIdRef above.
    if (loadRequestIdRef.current.templates !== requestId) return res;
    if (res?.error) {
      setTemplatesStatus('error');
      setTemplatesError(res.message || res.error);
      return res;
    }
    setTemplates(arr(res, 'templates').map((t) => ({ ...t, company_id: gid })));
    setTemplatesStatus('ready');
    setTemplatesError(null);
    loadedGroupRef.current.templates = gid;
    return res;
  }, []);

  // ICIB-03a — GET /notifications?company={gid} (paginated), scoped via the
  // documented `group` filter — note the param name: `group`, not
  // `company_id` (that's ICIB-03d's bulk-resend body field). Lazy, per
  // group — called by Overview and Follow-up. `include_resolved=true`
  // (added to the ICIB-03a contract) so a resolved row stays in `notifications`
  // after resolveNotification's refetch instead of vanishing — Follow-up
  // wants the full history, not just what's still open; openNotifs below
  // still filters back down to open-only for Overview's tile.
  const loadNotifications = useCallback(async (gid) => {
    const requestId = ++loadRequestIdRef.current.notifications;
    if (!gid) { setNotifications([]); setNotificationsStatus('ready'); setNotificationsError(null); loadedGroupRef.current.notifications = null; return null; }
    // Only skip the loading flash when re-fetching the *same* group already
    // held — see loadedGroupRef above.
    const sameGroup = loadedGroupRef.current.notifications === gid;
    setNotificationsStatus((s) => (sameGroup && s === 'ready' ? 'ready' : 'loading'));
    const res = await fetchAllPages(`/notifications?company=${gid}&include_resolved=true`, 'notifications');
    // Stale-response guard — see loadRequestIdRef above.
    if (loadRequestIdRef.current.notifications !== requestId) return res;
    if (res?.error) {
      setNotificationsStatus('error');
      setNotificationsError(res.message || res.error);
      return res;
    }
    setNotifications(arr(res, 'notifications'));
    setNotificationsStatus('ready');
    setNotificationsError(null);
    loadedGroupRef.current.notifications = gid;
    return res;
  }, []);

  // ICIB-12 — GET /companies/{gid}/enrolments (paginated, limit/offset, same
  // contract as ICIB-01a/GET /notifications — default 50, capped 200).
  // Fetched unfiltered — Confirmations needs both the `invited` and
  // `removed` rows at once, and the endpoint's `status` filter only takes
  // one value at a time, so it's simpler (and no more expensive, given
  // fetchAllPages walks every page either way) to filter client-side.
  const loadGroupEnrolments = useCallback(async (gid) => {
    const requestId = ++loadRequestIdRef.current.groupEnrolments;
    if (!gid) { setGroupEnrolments([]); setGroupEnrolmentsStatus('ready'); setGroupEnrolmentsError(null); loadedGroupRef.current.groupEnrolments = null; return null; }
    // Only skip the loading flash when re-fetching the *same* group already
    // held — see loadedGroupRef above.
    const sameGroup = loadedGroupRef.current.groupEnrolments === gid;
    setGroupEnrolmentsStatus((s) => (sameGroup && s === 'ready' ? 'ready' : 'loading'));
    const res = await fetchAllPages(`/companies/${gid}/enrolments`, 'enrolments');
    // Stale-response guard — see loadRequestIdRef above.
    if (loadRequestIdRef.current.groupEnrolments !== requestId) return res;
    if (res?.error) {
      setGroupEnrolmentsStatus('error');
      setGroupEnrolmentsError(res.message || res.error);
      return res;
    }
    setGroupEnrolments(arr(res, 'enrolments'));
    setGroupEnrolmentsStatus('ready');
    setGroupEnrolmentsError(null);
    loadedGroupRef.current.groupEnrolments = gid;
    return res;
  }, []);

  // ICIB-13a — GET /companies/{gid}/pending-intake (paginated, same
  // limit/offset/count contract as ICIB-12 above). Every scheduled
  // Addition/Update/Removal row for the group, whatever its status —
  // status/action filtering happens client-side in the view, same
  // reasoning as loadGroupEnrolments (the endpoint's filters only take one
  // value at a time, and fetchAllPages walks every page regardless).
  const loadPendingIntake = useCallback(async (gid) => {
    const requestId = ++loadRequestIdRef.current.pendingIntake;
    if (!gid) { setPendingIntake([]); setPendingIntakeStatus('ready'); setPendingIntakeError(null); loadedGroupRef.current.pendingIntake = null; return null; }
    const sameGroup = loadedGroupRef.current.pendingIntake === gid;
    setPendingIntakeStatus((s) => (sameGroup && s === 'ready' ? 'ready' : 'loading'));
    const res = await fetchAllPages(`/companies/${gid}/pending-intake`, 'pending_intake_rows');
    // Stale-response guard — see loadRequestIdRef above.
    if (loadRequestIdRef.current.pendingIntake !== requestId) return res;
    if (res?.error) {
      setPendingIntakeStatus('error');
      setPendingIntakeError(res.message || res.error);
      return res;
    }
    setPendingIntake(arr(res, 'pending_intake_rows'));
    setPendingIntakeStatus('ready');
    setPendingIntakeError(null);
    loadedGroupRef.current.pendingIntake = gid;
    return res;
  }, []);

  // ICIB-13b — DELETE /companies/{gid}/pending-intake/{id}. Only a still-
  // `pending` row can be cancelled (409 invalid_state otherwise, surfaced
  // generically via the caller's toast) — refetch pendingIntake either way
  // so a successful cancel's row flips to `cancelled` in place rather than
  // disappearing.
  function cancelPendingIntake(gid, pendingIntakeRowId) {
    return mutatingRefresh(`/companies/${gid}/pending-intake/${pendingIntakeRowId}`, { method: 'DELETE' }, [loadPendingIntake]);
  }

  // ICIB-15 — GET /companies/{gid}/overview. Not paginated — a single summary
  // object per group, so no fetchAllPages/loadRequestIdRef entry needed,
  // unlike the list loaders above.
  const loadGroupOverview = useCallback(async (gid) => {
    if (!gid) { setGroupOverviewStatus('ready'); setGroupOverviewError(null); return null; }
    setGroupOverviewStatus((s) => (s === 'ready' ? 'ready' : 'loading'));
    const res = await icibFetch(`/companies/${gid}/overview`);
    if (res?.error) {
      setGroupOverviewStatus('error');
      setGroupOverviewError(res.message || res.error);
      return res;
    }
    setGroupOverview((m) => ({ ...m, [gid]: res }));
    setGroupOverviewStatus('ready');
    setGroupOverviewError(null);
    return res;
  }, []);

  /* Rate limit — README §4.0: 10 bulk calls per minute per user, fail-open on read.
     Client-side pre-check only; the backend is still the source of truth. */
  function rateLimited() {
    const cutoff = Date.now() - 60000;
    rateLog.current = rateLog.current.filter((t) => t > cutoff);
    if (rateLog.current.length >= 10) return true;
    rateLog.current.push(Date.now());
    return false;
  }

  // ---------------------------------------------------------------- selectors
  const group = (id) => db.groups.find((g) => g.id === id);
  const curGroup = () => group(groupId);
  // No more shared Product catalog (§2.9 removed) — a GroupProduct is
  // fully self-contained, so this only ever resolves within whichever
  // group's products are currently loaded (`db.groupProducts`, the
  // *current* group's, per EMPTY_DB above). A product belonging to a
  // group other than the one currently selected won't resolve here —
  // callers already fall back to showing the raw id (`product(id)?.name
  // || id`) for exactly this case.
  const product = (id) => db.groupProducts.find((p) => p.id === id);
  // Reads off `employees` (see loadEmployees above), not `db` — empty
  // until some view's mount effect has actually called loadEmployees for
  // this group.
  const employeesIn = (gid) => employees.filter((e) => e.company_id === gid);
  // Reads off `filteredEmployees` (see loadFilteredEmployees above) — empty
  // until EmployeesView's dropdown has picked a status other than "all".
  const filteredEmployeesIn = (gid) => filteredEmployees.filter((e) => e.company_id === gid);
  // `employees` itself never carries enrolments (ICIB-01a summary is
  // deliberately bare — see loadEmployees above) — pull them from
  // whichever employees have had their ICIB-01b detail loaded
  // (employeeDetails, populated by loadEmployeeDetail / ensureEmployeeDetails).
  // An employee whose detail hasn't been fetched yet just contributes no
  // rows here.
  const allEnrolments = () => employees.flatMap((e) => (employeeDetails[e.id]?.enrolments || []).map((en) => ({ ...en, emp: e })));
  // One open notification per enrolment — defends against a daily
  // stale_invitation sweep (ICIB-03a's README ref) that raises a fresh
  // notification for an enrolment that already has one open instead of
  // reusing it, which would otherwise surface as the same person sitting
  // in the queue twice under two different notification ids. Keeps the
  // most recently created of the set. Reads off `notifications` (see
  // loadNotifications above), not `db`.
  const openNotifs = () => {
    const byEnrolment = new Map();
    for (const n of notifications) {
      if (n.resolved_at) continue;
      const existing = byEnrolment.get(n.enrolment_id);
      if (!existing || new Date(n.created_at) > new Date(existing.created_at)) byEnrolment.set(n.enrolment_id, n);
    }
    return [...byEnrolment.values()];
  };
  // Follow-up's full queue — every notification loadNotifications fetched
  // (open and resolved, since it now asks for `include_resolved=true`),
  // newest first. Only the open half gets openNotifs' per-enrolment
  // dedup — that guards against the daily sweep raising a second *live*
  // duplicate for the same enrolment; a resolved row is a closed, one-off
  // historical event, so every one of them stays, undeduped.
  const allNotifs = () => {
    const resolved = notifications.filter((n) => n.resolved_at);
    return [...openNotifs(), ...resolved].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };
  const gProducts = (gid) => db.groupProducts.filter((gp) => gp.company_id === gid);
  // Reads off `templates` (see loadTemplates above), not `db`.
  const activeTemplate = (gid) => templates.find((t) => t.company_id === gid && t.is_active);
  // Reads off `users` (see loadUsers above), not `db` — empty until
  // something has actually called loadUsers (currently just UsersView).
  const activeIcibAdmins = () => users.filter((u) => u.role === 'icib_admin' && u.is_active);

  // Resolves an enrolment from whichever local data already has it — the
  // current group's employees whose ICIB-01b detail has been loaded (via
  // allEnrolments above), or a previously-fetched ICIB-03b detail for one
  // outside the current group scope (e.g. from the notification queue).
  // Confirmations/onboarding actions rely on this resolving correctly
  // before they'll even attempt the request — see confirmOnboarding,
  // confirmRemoval, resendInvite below.
  // Shared by findEnrolment (cached lookup) and resolveEnrolment's fallback
  // (freshly-fetched lookup) below — same shape either way.
  function enrolmentFromDetail(d) {
    if (!d) return undefined;
    return {
      id: d.enrolment_id, status: d.status, product_id: d.product_id, invited_at: d.invited_at,
      emp: { id: d.employee?.employee_id, company_id: d.employee?.company_id, user: d.employee?.user },
    };
  }

  function findEnrolment(id) {
    const local = allEnrolments().find((e) => e.id === id);
    if (local) return local;
    return enrolmentFromDetail(enrolmentDetails[id]);
  }

  // ICIB-03b — GET /enrolments/{enrolment_id}. Resolves a notification's
  // enrolment_id to the person/group it belongs to, regardless of the
  // currently-selected group scope. Cached — call again to force a refetch.
  async function loadEnrolmentDetail(enrolmentId) {
    const res = await icibFetch(`/enrolments/${enrolmentId}`);
    if (!res?.error) setEnrolmentDetails((m) => ({ ...m, [enrolmentId]: res }));
    return res;
  }

  // Same resolution findEnrolment does, but falls back to fetching ICIB-03b
  // on demand when nothing local has it — for callers (confirmOnboarding,
  // confirmRemoval) that only have an enrolment_id to work with, e.g. a
  // row from ICIB-12's group-wide enrolment book (enrolment_id +
  // employee_name + product + status — no employee_id/company_id), unlike
  // FollowupView which already has this loaded before its buttons enable.
  async function resolveEnrolment(enrolmentId) {
    const local = findEnrolment(enrolmentId);
    if (local) return local;
    // Build the record from the fetch's own return value, not by re-reading
    // enrolmentDetails after the await — setEnrolmentDetails only takes
    // effect on the *next* render, so this function's closure over
    // enrolmentDetails is still the pre-fetch one at this point. Re-reading
    // it here used to return undefined on the very call that just fetched
    // the record, forcing the caller (confirmOnboarding/confirmRemoval) to
    // fail with "not_found" on the first click and only succeed on a
    // second click once a later render's closure saw the cached value.
    const fetched = await loadEnrolmentDetail(enrolmentId);
    return fetched?.error ? undefined : enrolmentFromDetail(fetched);
  }

  // ICIB-08i — GET /companies/{company_id}/employer-admins. Cached per group —
  // call again to force a refetch (e.g. after an assign/unassign).
  async function loadGroupAdmins(gid) {
    const res = await icibFetch(`/companies/${gid}/employer-admins`);
    if (!res?.error) setGroupAdmins((m) => ({ ...m, [gid]: res }));
    return res;
  }

  // ICIB-10c — GET /form-templates/{id}. A template's fields aren't part
  // of the list response (ICIB-10a) — fetch on demand when a template is
  // opened. Cached — call again (e.g. after a field edit) to force a refetch.
  async function loadTemplateDetail(templateId) {
    const res = await icibFetch(`/form-templates/${templateId}`);
    if (!res?.error) setTemplateDetails((m) => ({ ...m, [templateId]: res }));
    return res;
  }

  // ICIB-10a's `?active=true` filter — GET /companies/{gid}/form-templates
  // ?active=true — returns the group's active template *with fields
  // already included*, in one call. Replaces Intake's Addition form's old
  // loadTemplates (list) -> activeTemplate() -> loadTemplateDetail(tpl.id)
  // three-step dance with a single round-trip, since that form only ever
  // needed the currently-active version anyway. A group with no active
  // template yet 404s (`not_found`) — stored as `null` rather than
  // surfaced as an error, since that's an ordinary state (see FormsView's
  // "create the first one"), not a failure. Cached per group — call again
  // (e.g. after activating a different version) to force a refetch.
  // useCallback'd (like loadTemplates/loadEmployees above, unlike the
  // on-demand loadTemplateDetail/loadEnrolmentDetail-style caches below)
  // because AdditionPanel puts this function itself in a useEffect's
  // dependency array — a fresh reference every render would re-fire that
  // effect every render, refetching this endpoint in an infinite loop.
  const loadActiveTemplate = useCallback(async (gid) => {
    const res = await icibFetch(`/companies/${gid}/form-templates?active=true`);
    if (res?.error === 'not_found') { setActiveTemplates((m) => ({ ...m, [gid]: null })); return res; }
    if (!res?.error) setActiveTemplates((m) => ({ ...m, [gid]: res.template }));
    return res;
  }, []);

  // ICIB-01b — GET /companies/{company_id}/employees/{id}. The summary list
  // (ICIB-01a, used for `employees` in db) deliberately carries no
  // form_data or enrolments — this is the drill-down that has them, for
  // one person. Cached — call again (e.g. after removing an enrolment) to
  // force a refetch.
  async function loadEmployeeDetail(gid, employeeId) {
    const res = await icibFetch(`/companies/${gid}/employees/${employeeId}`);
    if (!res?.error) {
      // Normalize enrolment_id -> id here, once, so every consumer of
      // employeeDetails (this cache, employeeWithDetail, or a view reading
      // it directly) sees the same shape the rest of the app uses.
      const normalized = { ...res, enrolments: (res.enrolments || []).map((en) => ({ ...en, id: en.enrolment_id || en.id })) };
      setEmployeeDetails((m) => ({ ...m, [employeeId]: normalized }));
      return normalized;
    }
    return res;
  }

  // ICIB-11l — GET /companies/{company_id}/available-tokens. Returns exactly
  // the merge-token whitelist GroupProduct.email_body_template's save-time
  // validation accepts: the fixed seven-token `static` set only (first_name,
  // last_name, employer_name, insurer_link, insurer_scheme_reference,
  // information_pack_link, effective_date) — updated: `form_fields` (a
  // group's active-template field_keys) is gone. No `Employee.form_data`
  // field is ever a valid merge token any more, group-specific or
  // otherwise, so this response is identical for every group regardless of
  // its active FormTemplate — same fixed list `Product.default_email_body_
  // template` (no group context) already accepted. This also fixed a
  // latent bug: the whitelist never excluded `email` before, even though
  // it's never written to form_data — a template referencing `{{ email }}`
  // used to pass save-time validation but always rendered blank. Cached
  // per group, though the result no longer actually varies by group.
  async function loadAvailableTokens(gid) {
    const res = await icibFetch(`/companies/${gid}/available-tokens`);
    if (!res?.error) setAvailableTokens((m) => ({ ...m, [gid]: res }));
    return res;
  }

  // Merges a cached ICIB-01b detail (form_data + enrolments) onto its
  // ICIB-01a summary row, for views that need enrolments across a whole
  // group's roster (Confirmations, Overview's counts) rather than one
  // person at a time. Only reflects employees whose detail has actually
  // been loaded (see loadEmployeeDetail) — call ensureEmployeeDetails
  // first to load everyone in a group.
  function employeeWithDetail(emp) {
    const d = employeeDetails[emp.id];
    return d ? { ...emp, form_data: d.form_data, enrolments: (d.enrolments || []).map((en) => ({ ...en, id: en.enrolment_id || en.id })) } : emp;
  }

  function employeesWithEnrolments(gid) {
    return employeesIn(gid).map(employeeWithDetail);
  }

  // Fires loadEmployeeDetail for every employee in `gid` not already
  // cached. Awaiting this once (e.g. on mount) is what lets
  // employeesWithEnrolments actually return real enrolment data instead
  // of empty arrays.
  function ensureEmployeeDetails(gid) {
    return Promise.all(employeesIn(gid).filter((e) => !employeeDetails[e.id]).map((e) => loadEmployeeDetail(gid, e.id)));
  }

  // A bulk-upload's all-or-nothing rejection (§2.6) carries no top-level
  // `error` — just `{ committed: false, results: [...] }`, now passed
  // through as-is by icibFetch above instead of being replaced with a
  // generic error. `!res?.error` alone would therefore treat that
  // rejection as success and refetch for nothing actually written —
  // check `committed` too, wherever a bulk endpoint might be the caller.
  const mutationFailed = (res) => !!res?.error || res?.committed === false;

  /* Every mutating call below: send the request, and on success refetch
     `db` from the server rather than hand-patching local state — that way
     we don't have to guess each endpoint's exact response shape on top of
     guessing its path. */
  async function mutating(path, options) {
    const res = await icibFetch(path, options);
    if (!mutationFailed(res)) await refresh(groupId);
    return res;
  }

  // Same pattern as mutating() above, but for the ICIB-08 user-management
  // actions: refetch `users` (loadUsers), not the whole `db` — none of
  // these touch groups/products/employees/templates/notifications, and
  // users is no longer part of refresh()'s batch (see the module comment
  // / loadUsers).
  async function usersMutating(path, options) {
    const res = await icibFetch(path, options);
    if (!mutationFailed(res)) await loadUsers();
    return res;
  }

  // General form of the same pattern: send the request, and on success
  // refetch exactly the lazy resource(s) this particular action actually
  // changed (each a loadX(groupId) call), rather than the whole `db` or
  // guessing which of employees/templates/notifications is affected.
  // `loaders` is an array of loadEmployees/loadTemplates/loadNotifications
  // (or a mix, for an action that touches more than one).
  async function mutatingRefresh(path, options, loaders) {
    const res = await icibFetch(path, options);
    if (!mutationFailed(res)) await Promise.all(loaders.map((load) => load(groupId)));
    return res;
  }

  // ============================================================ ICIB-01 / 02
  // form_data isn't part of the ICIB-01a summary (`employees`) at all —
  // just employeeDetails, which the caller already reloads itself
  // (loadEmployeeDetail) after this succeeds — so nothing here needs
  // refetching.
  function saveFormData(employeeId, patch) {
    const gid = employees.find((e) => e.id === employeeId)?.company_id;
    return icibFetch(`/companies/${gid}/employees/${employeeId}/form-data`, { method: 'PATCH', body: { form_data: patch } });
  }

  // Personal-detail fields (name/dob/phone/address) ARE part of the
  // summary, unlike form_data above — refetch employees so the roster
  // reflects the correction. `patch.effective_date` (§2.6a) in the future
  // returns `202 scheduled` instead — no write happens yet, but refetch
  // pendingIntake too so the new scheduled row shows up there.
  function savePerson(employeeId, patch) {
    const gid = employees.find((e) => e.id === employeeId)?.company_id;
    return mutatingRefresh(`/companies/${gid}/employees/${employeeId}`, { method: 'PATCH', body: patch }, [loadEmployees, loadPendingIntake]);
  }

  // `fields.effective_date` (§2.6a) in the future returns `202 scheduled`
  // instead of creating the User/Employee/EmployeeEnrolment — refetch
  // pendingIntake too so it shows up there.
  function addOne(fields) {
    return mutatingRefresh(`/companies/${groupId}/employees`, { method: 'POST', body: fields }, [loadEmployees, loadPendingIntake]);
  }

  // ICIB-02c single-item — DELETE /companies/{company_id}/employees/{id}/enrolments/{enrolment_id}
  // (BrandonUserStoryAPI.md §ICIB-02c / README §4.4). Ends this one enrolment
  // (status -> removed) and raises the pending_removal_confirmation
  // notification — doesn't change the ICIB-01a summary itself (no
  // enrolments in it), so only notifications needs refetching here.
  // `effective_date`/`reason` (§2.6a) are required in the body even though
  // the target is identified by the URL — a future effective_date returns
  // `202 scheduled` instead (enrolment left untouched, no notification
  // raised yet), so refetch pendingIntake too in case that's what happened
  // — a harmless extra call on the immediate-effect path since nothing
  // new landed there.
  function removeEnrolment(employeeId, enrolmentId, { effective_date, reason } = {}) {
    const gid = employees.find((e) => e.id === employeeId)?.company_id;
    return mutatingRefresh(`/companies/${gid}/employees/${employeeId}/enrolments/${enrolmentId}`, { method: 'DELETE', body: { effective_date, reason } }, [loadNotifications, loadPendingIntake]);
  }

  // ICIB-02 bulk — POST /companies/{company_id}/employees/bulk-upload. One
  // endpoint handles all three actions: each row carries its own `action`
  // (ADDITION/UPDATE/REMOVAL), and a file can mix them freely — all
  // removals process before any addition/update regardless of row order
  // (README §2.6). All-or-nothing: nothing commits unless every row
  // validates; `rows` here is whatever parseCsv produced from the uploaded
  // file, keyed by the same headers the downloaded template uses.
  // Additions/updates change the roster, removals raise notifications —
  // a mixed file can do either, so refetch both. Any row's own
  // `effective_date` (§2.6a) can also land it in pending-intake instead
  // (`"status": "scheduled"` in that row's result) — refetch that too.
  function bulkUploadEmployees(fileName, rows) {
    if (rateLimited()) return Promise.resolve({ error: 'rate_limited', message: 'Too many bulk-upload requests. Limit is 10 per minute per user.' });
    return mutatingRefresh(`/companies/${groupId}/employees/bulk-upload`, { method: 'POST', body: { file: fileName, rows } }, [loadEmployees, loadNotifications, loadPendingIntake]);
  }

  // README §4.4 — GET /companies/{company_id}/employees/template. Not
  // implemented on the backend yet (no route), and no view calls this —
  // the bulk-upload panels build their .xlsx template client-side.
  function downloadEmployeesTemplate() {
    return icibDownload(`/companies/${groupId}/employees/template`, `${curGroup()?.company_code || 'employees'}_intake_template.csv`);
  }

  // ==================================================================== ICIB-03
  // ICIB-03c — "Never changes status or invited_at" (a resend is not a
  // new invitation) — nothing to refetch.
  function resendInvite(enrolmentId) {
    const en = findEnrolment(enrolmentId);
    if (!en) return Promise.resolve({ error: 'not_found', message: 'Only an open invitation can be resent.' });
    return icibFetch(`/companies/${en.emp.company_id}/employees/${en.emp.id}/enrolments/${enrolmentId}/invite`, { method: 'POST' });
  }

  // ICIB-03d — "Does not resolve the notifications it touches" — same as
  // above, nothing to refetch.
  function resendAll(gid) {
    if (rateLimited()) return Promise.resolve({ error: 'rate_limited', message: 'Too many resend requests. Limit is 10 per minute per user.' });
    const qs = gid ? `?company_id=${gid}` : '';
    return icibFetch(`/notifications/stale-invitations/resend${qs}`, { method: 'POST' });
  }

  function resolveNotification(notificationId) {
    return mutatingRefresh(`/notifications/${notificationId}/resolve`, { method: 'PATCH' }, [loadNotifications]);
  }

  // ==================================================================== ICIB-04
  // An onboarding/removal-confirmation outcome can auto-resolve the
  // matching notification (§2.12) — refetch notifications for that — and
  // always changes the row's status in ICIB-12's enrolment book, so
  // refetch groupEnrolments too (what ConfirmationsView, the only current
  // caller, reads). resolveEnrolment (above) is what lets this work from
  // just an enrolment_id — ICIB-12's rows don't carry employee_id/company_id,
  // unlike the ICIB-01a+01b roster this used before ICIB-12 existed.
  async function confirmOnboarding(enrolmentId, outcome) {
    const en = await resolveEnrolment(enrolmentId);
    if (!en) return { error: 'not_found', message: 'Enrolment not found.' };
    return mutatingRefresh(`/companies/${en.emp.company_id}/employees/${en.emp.id}/enrolments/${enrolmentId}/onboarding-status`, { method: 'PATCH', body: { onboarding_status: outcome } }, [loadNotifications, loadGroupEnrolments]);
  }

  async function confirmRemoval(enrolmentId) {
    const en = await resolveEnrolment(enrolmentId);
    if (!en) return { error: 'not_found', message: 'Enrolment not found.' };
    return mutatingRefresh(`/companies/${en.emp.company_id}/employees/${en.emp.id}/enrolments/${enrolmentId}/confirm-removal`, { method: 'PATCH' }, [loadNotifications, loadGroupEnrolments]);
  }

  // ICIB-04b bulk onboarding-status — each row identified by email + subsidised_product_level.
  function applyBulkOnboarding(rows) {
    if (rateLimited()) return Promise.resolve({ error: 'rate_limited', message: 'Too many bulk-upload requests. Limit is 10 per minute per user.' });
    return mutatingRefresh(`/companies/${groupId}/employees/enrolments/onboarding-status/bulk-upload`, { method: 'POST', body: { rows } }, [loadNotifications, loadGroupEnrolments]);
  }

  // ICIB-04d bulk confirm-removal — each row identified by email + subsidised_product_level.
  function applyBulkConfirmRemoval(rows) {
    if (rateLimited()) return Promise.resolve({ error: 'rate_limited', message: 'Too many bulk-upload requests. Limit is 10 per minute per user.' });
    return mutatingRefresh(`/companies/${groupId}/employees/enrolments/confirm-removal/bulk-upload`, { method: 'POST', body: { rows } }, [loadNotifications, loadGroupEnrolments]);
  }

  // ==================================================================== ICIB-09
  function createGroup({ name, company_code }) {
    return mutating('/companies', { method: 'POST', body: { name, company_code } });
  }

  function updateGroup(id, { name, company_code }) {
    return mutating(`/companies/${id}`, { method: 'PATCH', body: { name, company_code } });
  }

  function deactivateGroup(id) {
    return mutating(`/companies/${id}`, { method: 'DELETE' }).then((res) => {
      // Back to "All groups" rather than guessing another one to land
      // on — no auto-pick elsewhere either (see refresh() above).
      if (!res?.error && groupId === id) setGroupId(null);
      return res;
    });
  }

  // ICIB-09f — PATCH /companies/{id}/reactivate. Undoes deactivateGroup;
  // idempotent on an already-active group, same as deactivateGroup on an
  // already-inactive one.
  function reactivateGroup(id) {
    return mutating(`/companies/${id}/reactivate`, { method: 'PATCH' });
  }

  // ==================================================================== ICIB-08
  // `job_title` (§4.2, added) is optional and only valid alongside
  // company_id — stored on the new UserEmployerClient row this creates.
  // Rejected (invalid_role_for_company_assignment) if supplied without
  // company_id, same guard as job_title with role = icib_admin.
  // ICIB-08b — GET /users/{id}. Read-only check used when UsersView's
  // drawer opens, so a user deleted after the list loaded shows the
  // server's 404 ("User not found.") instead of the stale cached row.
  // Not cached: the list (`users`) stays the source of truth for display.
  function loadUser(userId) {
    return icibFetch(`/users/${userId}`);
  }

  function createUser({ first_name, last_name, email, phone, role, company_id, job_title }) {
    return usersMutating('/users', { method: 'POST', body: { first_name, last_name, email, phone: phone || undefined, role, company_id, job_title: company_id ? (job_title || undefined) : undefined } });
  }

  // `patch` may carry company_id + job_title together (§4.2, added) to
  // retarget the job_title stored on one of this user's group
  // assignments — the two are required together (400 invalid_field
  // otherwise) since job_title alone doesn't say which assignment it's
  // for. Can be combined with the personal-detail/role fields in the same
  // call, or sent on its own. Or `assignments` ([{ company_id, job_title }])
  // retargets several assignments' titles at once — not combinable with
  // the single company_id/job_title pair. The whole patch is saved
  // all-or-nothing (ICIB-08c); UsersView's drawer uses `assignments`.
  function updateUser(userId, patch) {
    return usersMutating(`/users/${userId}`, { method: 'PATCH', body: patch });
  }

  // `job_title` (§4.2, added) is optional — this person's job title at the
  // group being assigned here.
  function assignGroup(userId, groupIdToAssign, jobTitle) {
    return usersMutating(`/users/${userId}/assign-company`, { method: 'POST', body: { company_id: groupIdToAssign, job_title: jobTitle || undefined } });
  }

  function unassignGroup(userId, groupIdToRemove) {
    return usersMutating(`/users/${userId}/assign-company/${groupIdToRemove}`, { method: 'DELETE' });
  }

  function deactivateUser(userId) {
    return usersMutating(`/users/${userId}`, { method: 'DELETE' });
  }

  // ICIB-08n — PATCH /users/{id}/reactivate. Undoes deactivateUser; flips
  // is_active back to true and re-enables Entra sign-in if the user has an
  // entra_object_id. Idempotent on an already-active user, same as
  // deactivateUser on an already-inactive one.
  function reactivateUser(userId) {
    return usersMutating(`/users/${userId}/reactivate`, { method: 'PATCH' });
  }

  function resendSetup(userId) {
    return usersMutating(`/users/${userId}/resend-setup-email`, { method: 'POST' });
  }

  function resendAllSetup(role) {
    if (rateLimited()) return Promise.resolve({ error: 'rate_limited', message: 'Too many resend requests. Limit is 10 per minute per user.' });
    const qs = role ? `?role=${role}` : '';
    return usersMutating(`/users/resend-setup-email/bulk${qs}`, { method: 'POST' });
  }

  // ICIB-08g — GET /users/employer-admins/template. Fixed template columns
  // (email, first_name, last_name, phone, company_code, job_title — phone and
  // job_title optional, §4.2), same shape ICIB-08h's bulk-upload expects.
  function downloadEmployerAdminsTemplate() {
    return icibDownload('/users/employer-admins/template', 'employer_admins_template.csv');
  }

  // ICIB-08h — POST /users/employer-admins/bulk-upload. `rows` is
  // [{ email, first_name, last_name, phone, company_code, job_title }, ...]
  // parsed client-side from the uploaded file — first_name/last_name/phone
  // only populate a newly created User (never overwrite an existing one);
  // job_title optional (§4.2, added), stored only when the
  // row's assignment is newly created (a re-upload never overwrites an
  // existing title). All-or-nothing per §2.6 — role is always forced to
  // employer_admin server-side, never read from the file.
  function bulkUploadEmployerAdmins(fileName, rows) {
    if (rateLimited()) return Promise.resolve({ error: 'rate_limited', message: 'Too many bulk-upload requests. Limit is 10 per minute per user.' });
    return usersMutating('/users/employer-admins/bulk-upload', { method: 'POST', body: { file: fileName, rows } });
  }

  // ==================================================================== ICIB-10
  function createTemplateVersion(gid, name) {
    return mutatingRefresh(`/companies/${gid}/form-templates`, { method: 'POST', body: { name } }, [loadTemplates]);
  }

  // ICIB-10f also re-syncs the newly active template's product field
  // options to the company's current products (README §2.10), so a
  // cached detail for it (an open TemplateDrawer) is stale afterwards —
  // refetch it if we have one.
  function activateTemplate(gid, templateId) {
    return mutatingRefresh(`/companies/${gid}/form-templates/${templateId}/activate`, { method: 'PATCH' }, [loadTemplates]).then((res) => {
      if (!res?.error && templateDetails[templateId]) loadTemplateDetail(templateId);
      return res;
    });
  }

  function updateTemplateName(templateId, name) {
    return mutatingRefresh(`/form-templates/${templateId}`, { method: 'PATCH', body: { name } }, [loadTemplates]);
  }

  // ICIB-10e removed from the contract — it was DELETE /form-templates/{id}
  // (deactivate a template). Its own guard rejected acting on the
  // currently-active template, and every other template is already
  // is_active = false, so it could never actually change anything: the
  // only two outcomes were "already false → false" (a no-op) or a 409.
  // Every real active → inactive transition happens elsewhere — activateTemplate's
  // atomic swap above, and the same atomic swap each structural field
  // edit (addField/updateFields/removeField) performs internally.

  // ICIB-10g — POST /form-templates/{template_id}/fields, batched: takes
  // `fields: [...]` and produces exactly one new template version for the
  // whole call, not one version per field. Validated all-or-nothing —
  // one invalid field (including one colliding with another earlier in
  // the same batch) rejects the whole batch, nothing written. `fields`
  // is an array here even for the single-field case (the drawer's "Add a
  // custom field" form) — callers just send a one-item array.
  // Every structural edit (add/update/remove) creates a *new* template
  // version with its own id — the response's `template_id` (README §4.5).
  // Load that version's fields, not the old one's, so an open drawer can
  // switch to it (ICIB-10h). Older backends without `template_id` fall
  // back to reloading the template that was edited.
  function addField(templateId, fields) {
    return mutatingRefresh(`/form-templates/${templateId}/fields`, { method: 'POST', body: { fields } }, [loadTemplates]).then((res) => {
      if (!res?.error) loadTemplateDetail(res?.template_id || templateId);
      return res;
    });
  }

  // ICIB-10h — PATCH /form-templates/{template_id}/fields, same batching
  // shape as addField above: `fields: [{field_id, ...}, ...]`, one new
  // template version per call, all-or-nothing (a bad field_id or a
  // guaranteed-flag conflict anywhere in the batch rejects all of it).
  // Guaranteed-field flags are validated *across* the batch, so a swap —
  // clearing one field's flag while another in the same call picks it up
  // — is valid even though clearing it alone, with no replacement in the
  // same call, isn't.
  function updateFields(templateId, fields) {
    return mutatingRefresh(`/form-templates/${templateId}/fields`, { method: 'PATCH', body: { template_id: templateId, fields } }, [loadTemplates]).then((res) => {
      if (!res?.error) loadTemplateDetail(res?.template_id || templateId);
      return res;
    });
  }

  // Single-field convenience wrapper around updateFields above, for
  // callers (the field table's per-row Edit form) that only ever
  // touch one field at a time — same batch endpoint underneath, just a
  // one-item `fields` array.
  function updateField(templateId, fieldId, patch) {
    return updateFields(templateId, [{ field_id: fieldId, ...patch }]);
  }

  function removeField(templateId, fieldId) {
    return mutatingRefresh(`/form-fields/${fieldId}`, { method: 'DELETE' }, [loadTemplates]).then((res) => {
      if (!res?.error) loadTemplateDetail(res?.template_id || templateId);
      return res;
    });
  }

  // ==================================================================== ICIB-11
  // No more shared Product catalog (§2.9 removed) — every GroupProduct is
  // now created fully self-contained, directly under a group. `POST
  // /companies/{gid}/products` (ICIB-11g) requires all of name/insurer_name/
  // insurer_link/insurer_scheme_reference/eligibility_days/
  // email_body_template/information_pack_link — none of them has a
  // fallback any more, unlike the old catalog-backed attach call.
  function createGroupProduct(gid, { name, description, insurer_name, insurer_link, insurer_scheme_reference, eligibility_days, information_pack_link, email_body_template, email_subject_template }) {
    return mutating(`/companies/${gid}/products`, {
      method: 'POST',
      body: { name, description, insurer_name, insurer_link, insurer_scheme_reference, eligibility_days: Number(eligibility_days), information_pack_link, email_body_template, email_subject_template },
    });
  }

  // PATCH /companies/{company_id}/products/{product_id} (ICIB-11h) — partial
  // update, only fields present in `patch` change. None of them can be
  // cleared to null/blank once set (`required_field`, 400) — every field
  // is required for the row to exist at all now, so callers should only
  // ever send a replacement value, never `|| null`. The one exception is
  // the optional `email_subject_template`: blank resets it to the default.
  function updateGroupProduct(gid, productId, patch) {
    const body = { ...patch };
    if (body.eligibility_days !== undefined) body.eligibility_days = Number(body.eligibility_days);
    return mutating(`/companies/${gid}/products/${productId}`, { method: 'PATCH', body });
  }

  function detachProduct(gid, productId) {
    return mutating(`/companies/${gid}/products/${productId}`, { method: 'DELETE' });
  }

  // ICIB-11n — PATCH /companies/{company_id}/products/{product_id}/reactivate.
  // Undoes detachProduct — flips the existing row's is_active back to true
  // rather than creating a new one, keeping whatever it already had set
  // from before removal. Idempotent on an already-active product.
  function reactivateGroupProduct(gid, productId) {
    return mutating(`/companies/${gid}/products/${productId}/reactivate`, { method: 'PATCH' });
  }

  const refreshing = inFlightCount > 0;

  const value = {
    db, status, refresh, groupId, setGroupId, rateLimited,
    group, curGroup, product, employeesIn, allEnrolments, findEnrolment, openNotifs, gProducts, activeTemplate, activeIcibAdmins,
    enrolmentDetails, loadEnrolmentDetail, templateDetails, loadTemplateDetail, groupAdmins, loadGroupAdmins,
    employeeDetails, loadEmployeeDetail, employeesWithEnrolments, ensureEmployeeDetails,
    saveFormData, savePerson, addOne, removeEnrolment, bulkUploadEmployees, downloadEmployeesTemplate,
    resendInvite, resendAll, resolveNotification,
    confirmOnboarding, confirmRemoval, applyBulkOnboarding, applyBulkConfirmRemoval,
    createGroup, updateGroup, deactivateGroup,
    createUser, updateUser, assignGroup, unassignGroup, deactivateUser, reactivateUser, resendSetup, resendAllSetup,
    downloadEmployerAdminsTemplate, bulkUploadEmployerAdmins,
    createTemplateVersion, activateTemplate, updateTemplateName, addField, updateField, removeField,
    createGroupProduct, updateGroupProduct, detachProduct, reactivateGroupProduct,
    refreshing,
    users, usersStatus, usersError, loadUsers,
    employees, employeesStatus, employeesError, loadEmployees,
    templates, templatesStatus, templatesError, loadTemplates,
    notifications, notificationsStatus, notificationsError, loadNotifications,
    groupEnrolments, groupEnrolmentsStatus, groupEnrolmentsError, loadGroupEnrolments,
    updateFields,
    availableTokens, loadAvailableTokens,
    reactivateGroup,
    allNotifs,
    pendingIntake, pendingIntakeStatus, pendingIntakeError, loadPendingIntake, cancelPendingIntake,
    activeTemplates, loadActiveTemplate,
    groupOverview, groupOverviewStatus, groupOverviewError, loadGroupOverview,
    filteredEmployeesIn, filteredEmployeesStatus, filteredEmployeesError, loadFilteredEmployees,
    loadUser,
  };

  return <IcibAdminContext.Provider value={value}>{children}</IcibAdminContext.Provider>;
}

export function useIcibAdmin() {
  const ctx = useContext(IcibAdminContext);
  if (!ctx) throw new Error('useIcibAdmin must be used within an IcibAdminProvider');
  return ctx;
}
